import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ShareIcon from '@mui/icons-material/Share';
import StartIcon from '@mui/icons-material/Start';

import { SLIDE_PREVIEW_SIZE } from '@/constant';
import { Lesson } from '@/models/lesson';
import { useSession } from '@/providers/session/SessionProvider';
import { formatDistanceToNow } from '@/utils/format.date';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { Box, Checkbox, Chip, Divider, IconButton, Menu, MenuItem, Stack } from '@mui/material';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useOnHoverRef } from 'rooks';
import { Slide } from '../../../../components/slides/Slide';
import { isPointerPosition, PointerPosition } from '../../type';

const MENU_SPACING = 2;

export type Action = 'start' | 'rename' | 'remove' | 'share' | 'move' | 'duplicate' | 'unshare';

export type LessonCardActionProp = {
    onSelected?: (lesson: Lesson) => void;
    onPreviewClick?: (lesson: Lesson) => void;
    onActionClick?: (lesson: Lesson, action: Action) => void;
};

type LessonCardProp = {
    isSelecting?: boolean;
    isSelected?: boolean;
    lesson: Lesson;
};

export function LessonCard({ lesson, onPreviewClick, onActionClick, onSelected, isSelecting, isSelected }: LessonCardProp & LessonCardActionProp) {
    const { session } = useSession();
    const { t, i18n } = useTranslation();
    const slide = React.useMemo(() => lesson.latestLessonDraft.slides[0], [lesson.latestLessonDraft.slides]); // first page of the slide deck

    // ui state
    const [ctxtMenu, setCtxtMenu] = React.useState<HTMLElement | PointerPosition | null>(null);
    const [showCheckbox, setShowCheckbox] = React.useState(false);

    const onMenuSelected = (action: Action) => {
        if (onActionClick) {
            onActionClick(lesson, action);
        }
        setCtxtMenu(null);
    };

    const infoContainerRef = useOnHoverRef(
        () => {
            setShowCheckbox(true);
        },
        () => {
            setShowCheckbox(false);
        },
    );

    const actionRef = useOnHoverRef(
        () => {
            setShowCheckbox(false);
        },
        () => {
            setShowCheckbox(true);
        },
    );

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault();

        setCtxtMenu(
            ctxtMenu === null
                ? {
                      mouseX: event.clientX + 2,
                      mouseY: event.clientY - 6,
                  }
                : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
                  // Other native context menus might behave different.
                  // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
                  null,
        );

        // Prevent text selection lost after opening the context menu on Safari and Firefox
        const selection = document.getSelection();
        if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);

            setTimeout(() => {
                selection.addRange(range);
            });
        }
    };

    const SlidePreview = React.useMemo(() => {
        if (slide) {
            return <Slide key={slide.id} components={slide.data} preview />;
        }
        return <Box sx={{ backgroundColor: 'white', height: '100%', width: '100%' }} />;
    }, [slide]);

    return (
        <Card sx={{ padding: 0, position: 'relative' }} role='listitem' aria-labelledby={lesson.id} onContextMenu={handleContextMenu}>
            <CardActionArea
                onClick={(e) => {
                    e.stopPropagation();
                    onPreviewClick?.(lesson);
                }}
                sx={{
                    '&:hover': {
                        borderRadius: 0,
                    },
                }}
            >
                <CardMedia
                    component='div'
                    sx={{
                        width: SLIDE_PREVIEW_SIZE.width,
                        height: SLIDE_PREVIEW_SIZE.height,
                        backgroundColor: 'white',
                        overflow: 'hidden',
                    }}
                    aria-labelledby={lesson.id}
                >
                    {SlidePreview}
                </CardMedia>
            </CardActionArea>
            <Box
                sx={{ width: SLIDE_PREVIEW_SIZE.width }}
                onClick={() => {
                    if (isSelecting || showCheckbox) {
                        if (!lesson?.shareWithPublic) {
                            onSelected?.(lesson);
                        }
                    }
                }}
                ref={infoContainerRef}
            >
                <Stack direction='row' spacing={1} sx={{ p: 1, pl: 2 }}>
                    <Typography variant='h6' noWrap sx={{ alignContent: 'center', flexGrow: 1, textOverflow: 'ellipsis' }} id={lesson.id}>
                        {lesson.latestLessonDraft.lessonName}
                    </Typography>
                    <IconButton
                        aria-label='actions'
                        onClick={(e) => {
                            e.stopPropagation();
                            setCtxtMenu(e.currentTarget);
                        }}
                        ref={actionRef}
                    >
                        <MoreVertIcon />
                    </IconButton>
                    <Menu
                        open={ctxtMenu !== null}
                        anchorEl={ctxtMenu instanceof HTMLElement ? ctxtMenu : undefined}
                        anchorPosition={isPointerPosition(ctxtMenu) ? { top: ctxtMenu.mouseY, left: ctxtMenu.mouseX } : undefined}
                        anchorReference={isPointerPosition(ctxtMenu) ? 'anchorPosition' : 'anchorEl'}
                        onClose={() => setCtxtMenu(null)}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 12, // 8 for the button padding, 4 for the popver built-in top margin
                            horizontal: 'left',
                        }}
                    >
                        {session?.user?.teacherProfile && lesson.isOwnedBy(session.user.teacherProfile) ? (
                            <Box>
                                <MenuItem onClick={() => onMenuSelected('start')}>
                                    <Stack direction='row' gap={MENU_SPACING} sx={{ alignItems: 'center' }}>
                                        <StartIcon />
                                        <Typography>{t('lesson.actions.startLesson')}</Typography>
                                    </Stack>
                                </MenuItem>
                                <Divider />
                                <MenuItem onClick={() => onMenuSelected('rename')}>
                                    <Stack direction='row' gap={MENU_SPACING} sx={{ alignItems: 'center' }}>
                                        <DriveFileRenameOutlineIcon />
                                        <Typography>{t('lesson.actions.renameLesson')}</Typography>
                                    </Stack>
                                </MenuItem>
                                <MenuItem onClick={() => onMenuSelected('duplicate')}>
                                    <Stack direction='row' gap={MENU_SPACING} sx={{ alignItems: 'center' }}>
                                        <FileCopyIcon />
                                        <Typography>{t('lesson.actions.duplicateLesson')}</Typography>
                                    </Stack>
                                </MenuItem>
                                <MenuItem onClick={() => onMenuSelected('share')}>
                                    <Stack direction='row' gap={MENU_SPACING} sx={{ alignItems: 'center' }}>
                                        <ShareIcon />
                                        <Typography>{t('lesson.actions.share')}</Typography>
                                    </Stack>
                                </MenuItem>
                                <MenuItem onClick={() => onMenuSelected('move')}>
                                    <Stack direction='row' gap={MENU_SPACING} sx={{ alignItems: 'center' }}>
                                        <DriveFileMoveIcon />
                                        <Typography>{t('lesson.actions.move')}</Typography>
                                    </Stack>
                                </MenuItem>
                                <Divider />
                                <MenuItem onClick={() => onMenuSelected('remove')}>
                                    <Stack direction='row' gap={MENU_SPACING} sx={{ alignItems: 'center' }}>
                                        <DeleteIcon />
                                        <Typography>{t('lesson.actions.removeLesson')}</Typography>
                                    </Stack>
                                </MenuItem>
                            </Box>
                        ) : (
                            <Box>
                                <MenuItem onClick={() => onMenuSelected('start')}>
                                    <Stack direction='row' gap={MENU_SPACING} sx={{ alignItems: 'center' }}>
                                        <StartIcon />
                                        <Typography>{t('lesson.actions.startLesson')}</Typography>
                                    </Stack>
                                </MenuItem>

                                <MenuItem onClick={() => onMenuSelected('duplicate')}>
                                    <Stack direction='row' gap={MENU_SPACING} sx={{ alignItems: 'center' }}>
                                        <FileCopyIcon />
                                        <Typography>{t('lesson.actions.duplicateLesson')}</Typography>
                                    </Stack>
                                </MenuItem>
                                {!lesson?.shareWithPublic && !lesson.folder?.id && (
                                    <MenuItem
                                        onClick={() => {
                                            onMenuSelected('unshare');
                                        }}
                                    >
                                        <Stack direction='row' gap={MENU_SPACING} sx={{ alignItems: 'center' }}>
                                            <DeleteIcon />
                                            <Typography>{t('lesson.actions.removeLesson')}</Typography>
                                        </Stack>
                                    </MenuItem>
                                )}
                            </Box>
                        )}
                    </Menu>
                </Stack>
                <Stack spacing={1} sx={{ px: 2, pb: 2 }}>
                    {session?.user?.teacherProfile && lesson.isOwnedBy(session.user.teacherProfile) ? (
                        <Stack direction='row' spacing={1} sx={{ minHeight: 20 }}>
                            {lesson.isShared() && <Chip label={t('lesson.folder.tag.shared')} color='primary' />}
                        </Stack>
                    ) : (
                        <Stack direction='row' spacing={1} sx={{ minHeight: 20 }}>
                            <Chip label={t('lesson.tag.readOnly')} color='secondary' />
                        </Stack>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', minHeight: 20 }}>
                        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                            {t('common.lastUpdated')} {formatDistanceToNow(new Date(lesson.latestLessonDraft.dateUpdated), i18n.language)}
                        </Typography>
                        {(isSelecting || showCheckbox) && !lesson?.shareWithPublic && (
                            <Checkbox
                                checked={isSelected}
                                sx={{
                                    margin: 0,
                                    backgroundColor: (theme) => theme.palette.background.default,
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSelected?.(lesson);
                                }}
                            />
                        )}
                    </Box>
                </Stack>
            </Box>
        </Card>
    );
}
